import React from 'react';
import { IndexRoute, Route } from 'react-router';
import { HAS_WINDOW } from '@eb/feature-detection';

import Page from './Page';
import {
    BASE_URL,
    CHANGE_PLAN_DOWNGRADE_CONFLICTS_PATH,
    CHANGE_PLAN_DOWNGRADE_PATH,
    CHANGE_PLAN_NON_ADMIN_PATH,
    CHANGE_PLAN_PATH,
    CHANGE_PLAN_SALES_PATH,
    CHANGE_PLAN_UPDATE_PATH,
    ONBOARDING_PATH,
    OVERVIEW_PATH,
    PACKAGE_COMPARISON_PATH,
    PACKAGE_SELECTION_PATH,
    PRICING_PATH,
    SIGNUP_PATH,
} from '../constants/main';
import loadable from '@loadable/component';
import {
    ConfirmPlanConnectedDowngradePage,
    ConfirmPlanConnectedUpdatePage,
    ConnectedUpdatePage,
} from '../containers';

const ConnectedAccessPage = loadable(() => import('../containers/access/Page'));
const ConnectedSalesPage = loadable(() => import('../containers/sales/Page'));
const ConnectedPackageComparisonPage = loadable(() =>
    import('../containers/packageComparison/Page'),
);
const ConnectedPricingPage = loadable(() =>
    import('../containers/pricing/Page'),
);
const ConnectedSignUpPage = loadable(() => import('../containers/signUp/Page'));
const ConnectedPackageSelectionPage = loadable(() =>
    import('../containers/packageSelection/PackageSelectionPage'),
);
const ConnectedChangePlanPage = loadable(() =>
    import('../containers/changePlan/Page'),
);
const ConnectedDowngradePage = loadable(() =>
    import('../containers/downgrade/Page'),
);
const ConnectedPackageDowngradeConflictsPage = loadable(() =>
    import('../containers/downgradeConflicts/ConnectedPage'),
);

import { ConnectedOnboardingPage } from '../containers/onboarding';
import {
    checkAccess,
    checkChangePlanAccess,
} from '../containers/onboarding/utils';
import {
    canUpdateEssentialsV2,
    isPackageProfessional,
} from '../containers/utils';

const redirectToWagtailOverview = () => {
    if (HAS_WINDOW) {
        window.location.replace(`${BASE_URL}${OVERVIEW_PATH}`);
    }
};

const getDowngradeRoutes = (
    isPackageDowngradeEnabled = false,
    checkChangePlanAccessOnEnter,
) => {
    if (!isPackageDowngradeEnabled) {
        return (
            <Route
                path={CHANGE_PLAN_DOWNGRADE_PATH}
                component={ConnectedDowngradePage}
                onEnter={checkChangePlanAccessOnEnter}
            />
        );
    }

    return (
        <Route path={CHANGE_PLAN_DOWNGRADE_PATH}>
            <IndexRoute
                component={ConfirmPlanConnectedDowngradePage}
                onEnter={checkChangePlanAccessOnEnter}
            />
            <Route
                path={CHANGE_PLAN_DOWNGRADE_CONFLICTS_PATH}
                component={ConnectedPackageDowngradeConflictsPage}
                onEnter={checkChangePlanAccessOnEnter}
            />
        </Route>
    );
};

const getRoutes = (pageInfo, store, isBaseUrl) => {
    if (HAS_WINDOW) {
        isBaseUrl = window.location.pathname === BASE_URL;
    }

    const {
        env: { locale },
        assortments: { assortmentsAvailability },
        featureFlags,
    } = store.getState();

    const PageWithInfo = (props) => <Page {...pageInfo} {...props} />;

    const checkAccessOnEnter = (nextRouterState, replace) => {
        checkAccess(store, replace);
    };

    const checkChangePlanAccessOnEnter = (nextRouterState, replace) => {
        checkChangePlanAccess(store, replace);
    };

    const UpdatePage = featureFlags.isPackageChangeDowngradeEnabled
        ? ConfirmPlanConnectedUpdatePage
        : ConnectedUpdatePage;

    const essentialsV1UpdateRoute = canUpdateEssentialsV2({
        locale,
        assortmentsAvailability,
    }) ? (
        <Route
            path={CHANGE_PLAN_UPDATE_PATH}
            component={UpdatePage}
            onEnter={checkChangePlanAccessOnEnter}
        />
    ) : null;

    const downgradeRoute = isPackageProfessional(assortmentsAvailability)
        ? getDowngradeRoutes(
              featureFlags.isPackageChangeDowngradeEnabled,
              checkChangePlanAccessOnEnter,
          )
        : null;

    if (isBaseUrl) {
        return <Route path={BASE_URL} onEnter={redirectToWagtailOverview} />;
    }

    return (
        <Route path={BASE_URL}>
            <Route component={PageWithInfo}>
                <Route path={PRICING_PATH} component={ConnectedPricingPage} />
                <Route
                    path={PACKAGE_COMPARISON_PATH}
                    component={ConnectedPackageComparisonPage}
                />
                <Route
                    path={ONBOARDING_PATH}
                    component={ConnectedOnboardingPage}
                    onEnter={checkAccessOnEnter}
                />
                <Route
                    path={PACKAGE_SELECTION_PATH}
                    component={ConnectedPackageSelectionPage}
                />
                <Route path={SIGNUP_PATH} component={ConnectedSignUpPage} />
                <Route
                    path={CHANGE_PLAN_PATH}
                    component={ConnectedChangePlanPage}
                    onEnter={checkChangePlanAccessOnEnter}
                />
                <Route
                    path={CHANGE_PLAN_SALES_PATH}
                    component={ConnectedSalesPage}
                />
                <Route
                    path={CHANGE_PLAN_NON_ADMIN_PATH}
                    component={ConnectedAccessPage}
                />
                {essentialsV1UpdateRoute}
                {downgradeRoute}
            </Route>
            <Route path="*" onEnter={redirectToWagtailOverview} />
        </Route>
    );
};

export default getRoutes;
